import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { errorDescriptionsMap } from './shared/utils';

export type ProductFlagResponse = {
  cp_products: boolean;
  doctors_products: boolean;
};
export interface UserinfoResponse {
  sub: string;
  given_name: string;
  family_name?: any;
  nickname: string;
  name: string;
  picture: string;
  gender?: any;
  birthdate: string;
  updated_at: string;
  email: string;
  email_verified: boolean;
  'https://id.dx-utility.com/custom': Httpsiddxutilitycomcustom;
}

interface Httpsiddxutilitycomcustom {
  profile: Profile[];
  role: string;
  user_id: string;
}

interface Profile {
  label: string;
  name: string;
  value: Value[] | string;
}

interface Value {
  label: string;
  value: string;
}
@Injectable({
  providedIn: 'root',
})
export class DxauthService {
  constructor(private http: HttpClient) {}

  private static toPattern(p: string) {
    return `(?=.*[${p}])`;
  }

  static buildRegex({ minimumLength }: { minimumLength: number }) {
    const _lower = 'a-z';
    const _upper = 'A-Z';
    const _number = '0-9';
    const _symbol = '!-/:-@[-`{-~';
    const lower = this.toPattern(_lower);
    const upper = this.toPattern(_upper);
    const number = this.toPattern(_number);
    const symbol = this.toPattern(_symbol);

    // TODO combinationを組み合わせるライブラリを使って動的に組み立てられるように
    const cominationPattern = [
      [lower, upper, number],
      [lower, upper, symbol],
      [upper, number, symbol],
      [lower, number, symbol],
    ];
    const last = [_lower, _upper, _number, _symbol].join('');
    return `^(${cominationPattern
      .map((p) => p.join(''))
      .join('|')})([${last}]){${minimumLength},}$`;
  }

  static passwordValidator(
    password: string,
    options: {
      /** 文字の重複を禁じるか否か */
      isCharaUnique?: boolean;
      /** 最低文字数 */
      minLength: number;
    } = {
      minLength: 8,
    }
  ):
    | {
        isValid: true;
      }
    | {
        isValid: false;
        errorMessage: string;
        code: 'TOO_SHORT' | 'CHARA_UNIQUE' | 'INVALID_FORMAT';
      } {
    // 最低文字数に満たさない場合
    if (password.length < options.minLength) {
      console.log(password.length, options.minLength);

      return {
        isValid: false,
        errorMessage: `${options.minLength}文字以上で入力してください`,
        code: 'TOO_SHORT',
      };
    }

    // 文字の重複を禁じる場合
    if (options.isCharaUnique) {
      const uniqueChars = new Set(password);
      // 重複がある場合
      if (uniqueChars.size !== password.length) {
        return {
          isValid: false,
          errorMessage: '「同じ文字を二回以上使用することはできません。',
          code: 'CHARA_UNIQUE',
        };
      }
    }

    // 半角英字小文字,半角英字大文字,数字, 記号[!-/:-@[-`{-~]のうちを３種類以上含めること. かつ　minLength文字以上であること
    // 正規表現パターン作成
    const passwordRegex = new RegExp(
      this.buildRegex({ minimumLength: options.minLength })
    );

    const isValid = passwordRegex.test(password);
    if (!isValid) {
      return {
        isValid: false,
        errorMessage: `半角英数字と記号を３種類以上含めた、${options.minLength}文字以上のパスワードを入力してください`,
        code: 'INVALID_FORMAT',
      };
    }
    return {
      isValid: true,
    };
  }

  async geIntegratedFlagByEmail(email: string) {
    return (await this.http
      .post<ProductFlagResponse>(
        `https://${environment.domain}/management/api/v1/auth/products`,
        {
          email,
        }
      )
      .toPromise()) as {
      cp_products: boolean;
      doctors_products: boolean;
    };
  }

  async geIntegratedFlagByAccessToken(accessToken: string) {
    return (await this.http
      .get(`https://${environment.domain}/management/api/v1/auth/products`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .toPromise()) as {
      cp_products: boolean;
      doctors_products: boolean;
    };
  }

  async resendInviteEmail(params: { clientId: string; email: string }) {
    await this.http
      .post(
        `https://${environment.domain}/management/api/v1/auth/verification-email`,
        {
          client_id: params.clientId,
          email: params.email,
        }
      )
      .toPromise();
  }

  async isValidEmailAndPassword(params: {
    clientId: string;
    email: string;
    password: string;
    connection?: string;
  }) {
    try {
      const response = await this.http
        .post(`https://${environment.domain}/co/authenticate`, {
          client_id: params.clientId,
          username: params.email,
          password: params.password,
          realm: params.connection || 'Username-Password-Authentication',
          credential_type: 'http://auth0.com/oauth/grant-type/password-realm',
        })
        .toPromise();
      return response;
    } catch (e) {
      const error = e as {
        error: { error: string; error_description: string };
      };
      throw errorDescriptionsMap[error?.error?.error_description];
    }
  }

  // メールアドレスが登録済みかどうかをチェックする
  async isEmailExists(params: { email: string }) {
    try {
      const response = await this.http
        .post<{ isExist: boolean }>(
          `https://${environment.domain}/management/api/v1/auth/email_exist`,
          {
            email: params.email,
          }
        )
        .toPromise();
      if (response.isExist) return true;
      return false;
    } catch (e) {
      return false;
    }
  }

  /** 指定したアプリケーションにログイン or 新規登録したことがあるか */
  async isLoginedToApplication(params: { email: string; clientId: string }) {
    try {
      const response = await this.http
        .post<{
          is_logined: boolean;
        }>(
          `https://${environment.domain}/management/api/v1/auth/is_logined_to_application`,
          {
            email: params.email,
            client_id: params.clientId,
          }
        )
        .toPromise();
      return response;
    } catch (e) {
      return undefined;
    }
  }

  // ByAccessToken(accessToken: string) {
  async isLoginedToApplicationByAccessToken(params: {
    clientId: string;
    accessToken: string;
  }) {
    try {
      const response = await this.http
        .post<{
          is_logined: boolean;
        }>(
          `https://${environment.domain}/management/api/v1/auth/is_logined_to_application`,
          {
            client_id: params.clientId,
          },
          {
            headers: {
              Authorization: `Bearer ${params.accessToken}`,
            },
          }
        )
        .toPromise();
      return response;
    } catch (e) {
      return undefined;
    }
  }

  async userinfo(params: { accessToken: string }) {
    try {
      return this.http
        .get<UserinfoResponse>(`https://${environment.domain}/userinfo`, {
          headers: {
            Authorization: `Bearer ${params.accessToken}`,
          },
        })
        .toPromise();
    } catch (error) {}
  }

  /** DxAuthで扱っている診療科一覧を取得する */
  async getDepartments() {
    return this.http
      .get<{
        departments: {
          value: string;
          label: string;
          category: string;
        }[];
      }>(`https://${environment.domain}/management/api/v1/departments`)
      .toPromise();
  }

  /** Nextへの新規登録時にNext APIへ通知する処理. ログイン画面→DxAuth API→Next APIの流れで実行するため、このメソッドの呼び出し自体はDxAuth向けに呼び出す */
  async dnextRegisterCallbck(params: {
    auth0_sub: string;
    industry_type: string;
    departments: string[];
    postal_code?: string;
    prefectures: string;
    city_address: string;
    building_name: string;
    job_title_select: string;
  }) {
    return this.http
      .post(
        `https://${environment.domain}/management/api/v1/auth/registration/dnext/callback`,
        params
      )
      .toPromise();
  }

  /**
   * パスワードリセットリクエストAPIを呼び出す
   * @param email 対象ユーザーのメールアドレス
   * @param connection 対象ユーザーのCONNECTION
   */
  async changePassword({
    email,
    connection,
  }: {
    email: string;
    connection?: string;
  }): Promise<{ result: boolean }> {
    return this.http
      .post<{ result: boolean }>(
        `https://${environment.domain}/management/api/v1/auth/events/post-password-change-request`,
        {
          email,
          connection,
        }
      )
      .toPromise();
  }
}
